// Language loader utility
// Dynamically loads the appropriate language file based on URL parameter or default config

import { gameConfig } from '../config/gameConfig';

/**
 * Loads the appropriate language file based on URL parameter or default configuration
 * @returns {Promise<Object>} The language object from the loaded language file
 */
export const loadLanguage = async () => {
    // Parse URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    
    // Determine which language to use
    let languageCode = langParam || gameConfig.defaultLanguage;
    
    // Validate language code (basic validation)
    if (!languageCode || languageCode.trim() === '') {
        languageCode = gameConfig.defaultLanguage;
    }
    
    // Clean the language code (remove any special characters)
    languageCode = languageCode.toLowerCase().trim();
    
    if (gameConfig.debugMode) {
        console.log(`🌐 Loading language: ${languageCode}${langParam ? ' (from URL)' : ' (default)'}`);
    }
    
    // Try to load the language file
    try {
        const languageModule = await import(`../config/lang.${languageCode}.js`);
        
        if (gameConfig.debugMode) {
            console.log(`✅ Language loaded successfully: ${languageCode}`);
        }
        
        return languageModule.language;
    } catch (error) {
        // If the language file doesn't exist, fall back to default
        if (languageCode !== gameConfig.defaultLanguage) {
            console.warn(`⚠️ Language file not found: lang.${languageCode}.js`);
            console.warn(`⚠️ Falling back to default language: ${gameConfig.defaultLanguage}`);
            
            try {
                const defaultLanguageModule = await import(`../config/lang.${gameConfig.defaultLanguage}.js`);
                return defaultLanguageModule.language;
            } catch (defaultError) {
                console.error(`❌ Failed to load default language file: lang.${gameConfig.defaultLanguage}.js`);
                throw new Error('Failed to load language files');
            }
        } else {
            console.error(`❌ Failed to load default language file: lang.${gameConfig.defaultLanguage}.js`);
            throw error;
        }
    }
};

/**
 * Gets the current language code from URL or config
 * @returns {string} The current language code
 */
export const getCurrentLanguageCode = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('lang') || gameConfig.defaultLanguage;
};

